import { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";
import { works } from "@/mocks/works";
import { characters } from "@/mocks/characters";

const genres = ["全部", "宫廷", "恋爱", "奇幻", "治愈", "都市", "日常", "武侠", "悬疑", "冒险", "美食", "强强", "宫斗"];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "韩国妖精漫画作品大全",
  description: "韩国妖精漫画代表作品盘点，覆盖宫廷谋略、都市怪谈、治愈日常等类型，附作品女主与CP介绍。",
  isPartOf: {
    "@type": "WebSite",
    name: "萌妖图鉴",
    url: "https://mangami2.github.io/",
  },
};

export default function Works() {
  const { hash } = useLocation();

  usePageMeta(
    "韩国妖精漫画作品大全｜人气妖精漫画盘点与女主介绍",
    "韩国妖精漫画作品大全，盘点宫廷谋略、都市怪谈、治愈日常等类型的人气作品，每部作品附女主角色与CP组合介绍，帮你快速找到想看的妖精漫画。",
    jsonLd
  );

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    }
  }, [hash]);

  const list = useMemo(() => works, []);

  const charactersOf = (workSlug: string) => characters.filter((c) => c.workSlug === workSlug);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">作品大全</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">韩国妖精漫画作品大全</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              从宫廷谋略到都市怪谈，从治愈日常到强强对峙，盘点值得一看的韩国妖精漫画代表作品，并附上各部作品的漂亮女主与CP介绍。
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {genres.map((g) => (
                <a
                  key={g}
                  href="#works"
                  className="rounded-full bg-background-50 px-4 py-1.5 text-sm font-medium text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700 whitespace-nowrap"
                >
                  {g}
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="works" className="mx-auto max-w-7xl scroll-mt-20 px-4 md:px-6 py-8 md:py-12">
          <div className="flex flex-col gap-6">
            {list.map((w, idx) => (
              <article
                key={w.slug}
                id={w.slug}
                className="scroll-mt-24 grid grid-cols-1 gap-6 rounded-3xl border border-background-200 bg-background-50 p-5 md:p-6 lg:grid-cols-[280px_1fr]"
              >
                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-background-100 lg:aspect-auto lg:min-h-[380px]">
                  <img
                    src={w.cover}
                    alt={`韩国妖精漫画《${w.title}》封面`}
                    title={`《${w.title}》`}
                    className="h-full w-full object-cover object-top"
                  />
                  <span
                    className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-medium text-background-50 ${
                      w.status === "已完结" ? "bg-secondary-500" : "bg-primary-500"
                    }`}
                  >
                    {w.status}
                  </span>
                </div>

                <div className="flex flex-col">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">《{w.title}》</h2>
                    <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-sm font-semibold text-accent-800">
                      <i className="ri-star-fill" aria-hidden="true"></i>
                      {w.rating.toFixed(1)}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-foreground-500">
                    {w.chapters} · {w.genre.join(" / ")}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {w.tags.map((t) => (
                      <span key={t} className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600">
                        {t}
                      </span>
                    ))}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-foreground-700">{w.intro}</p>

                  <div className="mt-5 border-t border-background-100 pt-4">
                    <h3 className="text-sm font-semibold text-foreground-950">本作女主与CP</h3>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {charactersOf(w.slug).map((c) => (
                        <Link
                          key={c.slug}
                          to={`/character/${c.slug}`}
                          className="group flex items-center gap-3 rounded-2xl border border-background-200 p-2.5 pr-4 transition-all hover:border-primary-300 hover:bg-background-100/60"
                        >
                          <img src={c.avatar} alt={`${c.name} 头像`} className="h-12 w-12 rounded-xl object-cover" />
                          <div>
                            <p className="text-sm font-semibold text-foreground-950 group-hover:text-primary-700">{c.name}</p>
                            <p className="text-xs text-foreground-500">{c.title}</p>
                          </div>
                          <i className="ri-arrow-right-s-line ml-1 text-foreground-400" aria-hidden="true"></i>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {idx === 0 && <div className="sr-only">韩国妖精漫画作品《{w.title}》介绍</div>}
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}